import { CheckCircle, AlertCircle, Mail, Shield, Calendar, User as UserIcon, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { format } from "date-fns";

interface Props {
  isAdmin: boolean;
}

export function AccountSection({ isAdmin }: Props) {
  const { user } = useAuth();
  const { subscription } = useSubscription();

  const hasActiveSub = subscription && subscription.tier !== "welcome";
  const initials = (user?.email ?? "??")
    .split("@")[0]
    .split(/[._-]/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  type Row = {
    label: string;
    icon: typeof Mail;
    value: string;
    mono?: boolean;
    badge?: React.ReactNode;
    tone?: "success" | "warn";
  };

  const rows: Row[] = [
    {
      label: "Email",
      icon: Mail,
      value: user?.email ?? "—",
      mono: true,
      badge: (
        <Badge className="gap-1 bg-success/10 text-success border border-success/20 hover:bg-success/15">
          <CheckCircle className="h-3 w-3" />
          Verified
        </Badge>
      ),
    },
    {
      label: "Add-in access",
      icon: hasActiveSub ? CheckCircle : AlertCircle,
      value: hasActiveSub
        ? "You can use this account in the Outlook Add-in"
        : subscription?.tier === "welcome"
        ? "Welcome credits available — subscribe for full access"
        : "Active subscription required for Add-in access",
      tone: hasActiveSub ? "success" : "warn",
    },
    ...(user?.created_at
      ? [{
          label: "Member since",
          icon: Calendar,
          value: format(new Date(user.created_at), "MMMM d, yyyy"),
        } as Row]
      : []),
    ...(isAdmin
      ? [{
          label: "Admin access",
          icon: Shield,
          value: "You have administrator privileges",
          badge: (
            <Badge variant="secondary" className="gap-1">
              <Shield className="h-3 w-3" />
              Admin
            </Badge>
          ),
        } as Row]
      : []),
  ];

  return (
    <div className="space-y-5">
      {/* Profile hero */}
      <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-primary/5 via-card to-card p-6 sm:p-7 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-5 flex-wrap">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-primary-glow text-primary-foreground flex items-center justify-center text-xl font-semibold shadow-fluent-4 shrink-0">
            {initials || <UserIcon className="h-7 w-7" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Signed in as</p>
            <h2 className="text-xl sm:text-2xl font-semibold truncate">{user?.email}</h2>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge className="gap-1 bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15">
                <Sparkles className="h-3 w-3" />
                {subscription?.tier === "welcome"
                  ? "Welcome plan"
                  : subscription
                  ? `${subscription.tier.charAt(0).toUpperCase()}${subscription.tier.slice(1)} plan`
                  : "No subscription"}
              </Badge>
              {isAdmin && (
                <Badge variant="secondary" className="gap-1">
                  <Shield className="h-3 w-3" />
                  Administrator
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail rows */}
      <div className="rounded-xl border border-border/60 bg-card divide-y divide-border/60 overflow-hidden">
        {rows.map((r, idx) => {
          const Icon = r.icon;
          return (
            <div key={idx} className="flex items-start gap-4 p-4 sm:p-5 hover:bg-muted/30 transition-colors">
              <div
                className={
                  "mt-0.5 h-9 w-9 rounded-lg flex items-center justify-center shrink-0 " +
                  (r.tone === "warn"
                    ? "bg-warning/10"
                    : r.tone === "success"
                    ? "bg-success/10"
                    : "bg-muted/60")
                }
              >
                <Icon
                  className={
                    r.tone === "warn"
                      ? "h-4 w-4 text-warning"
                      : r.tone === "success"
                      ? "h-4 w-4 text-success"
                      : "h-4 w-4 text-muted-foreground"
                  }
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{r.label}</p>
                <p className={`text-sm text-muted-foreground mt-0.5 break-words ${r.mono ? "font-mono" : ""}`}>
                  {r.value}
                </p>
              </div>
              {r.badge && <div className="shrink-0">{r.badge}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
